/**
 * O MODO PROJETOR — o telão da sala não é o notebook de quem acompanha.
 *
 * Pedido em 14/10, com as comemorações no ar: no projetor, a letra do top 3 do
 * pódio fica ilegível. O motivo está medido, e é o piso do `clamp()`: em
 * 1024×768, `.82vw` dá 8,4 px e o CSS cai no mínimo de 11 px — as 24 linhas do
 * pódio inteiro saem em 11 px, do mesmo tamanho em que sairiam num celular.
 *
 * A saída NÃO é aumentar a fonte para todo mundo: a mesma tela é vista em
 * notebook e celular, onde 24 linhas em 34 px não caberiam. Então o telão ganha
 * um PERFIL próprio, que muda duas coisas:
 *
 *   - **menos linhas**: na comemoração geral, só o CAMPEÃO de cada marca (8
 *     linhas em vez de 24); na festa por marca, o top 5 em vez do top 15;
 *   - **escala maior**: o CSS de `[data-projetor="1"]` sobe os pisos e troca a
 *     medida de `vw` para `vh` — no projetor o que estoura é a altura.
 *
 * **Não há detecção automática por largura, de propósito.** O palpite óbvio
 * seria "largura ≤ 1366 é projetor", e ele pega em cheio o notebook 1366×768 de
 * quem está acompanhando — justamente quem não pode perder densidade. O modo é
 * declarado: `?projetor=1` no endereço do telão, ou a tecla `P` na sala.
 *
 * Fica em `localStorage` porque o telão passa dias ligado e a página recarrega
 * (deploy, F5, reconexão do SSE): ligado uma vez, continua ligado.
 */

/** Onde o modo fica guardado entre recargas. */
export const CHAVE_PROJETOR = 'warroom.projetor'

/** Quantos representantes o pódio da comemoração geral mostra por marca. */
export const POR_MARCA_NO_PODIO_PROJETOR = 1

/** Quantos representantes a festa de uma marca mostra. */
export const REPRESENTANTES_NA_FESTA_PROJETOR = 5

/**
 * O que o endereço pede: `true`, `false` ou `null` (não falou nada).
 *
 * `?projetor=1` liga, `?projetor=0` desliga — e desligar pelo endereço precisa
 * existir, senão um telão ligado por engano só volta limpando `localStorage` na
 * mão, no meio do evento.
 */
export function projetorNoEndereco(busca: string): boolean | null {
  let bruto: string | null = null
  try {
    bruto = new URLSearchParams(busca ?? '').get('projetor')
  } catch {
    return null
  }
  if (bruto === null) return null
  const limpo = bruto.trim().toLowerCase()
  if (limpo === '' || limpo === '1' || limpo === 'sim' || limpo === 'true') return true
  if (limpo === '0' || limpo === 'nao' || limpo === 'não' || limpo === 'false') return false
  /* Valor que não se entende NÃO liga o modo: ligar por engano tira 16 linhas
     do pódio, e ninguém na sala vai saber por quê. */
  return null
}

/** Lê o modo guardado. Falha em silêncio, como o resto do `localStorage`. */
export function lerProjetorGuardado(armazem: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return armazem?.getItem(CHAVE_PROJETOR) === '1'
  } catch {
    /* `localStorage` lança em janela com cookies bloqueados e no modo privado
       de alguns navegadores. Sem memória, o modo vale só para esta sessão. */
    return false
  }
}

/** Grava o modo. Falha em silêncio, pela mesma razão. */
export function gravarProjetor(armazem: Pick<Storage, 'setItem' | 'removeItem'> | null, ligado: boolean): void {
  try {
    if (ligado) armazem?.setItem(CHAVE_PROJETOR, '1')
    else armazem?.removeItem(CHAVE_PROJETOR)
  } catch {
    /* idem */
  }
}

/**
 * O estado inicial: o endereço manda, e o guardado vale quando ele cala.
 *
 * Nessa ordem porque o endereço é o que alguém acabou de digitar — e é assim
 * que se corrige um telão que ficou no modo errado, sem abrir o console.
 */
export function modoInicial(
  busca: string,
  armazem: Pick<Storage, 'getItem'> | null,
): boolean {
  const pedido = projetorNoEndereco(busca)
  return pedido === null ? lerProjetorGuardado(armazem) : pedido
}

/** Quantas linhas por marca o pódio da comemoração geral mostra. */
export function porMarcaNoPodio(projetor: boolean, padrao: number): number {
  return projetor ? POR_MARCA_NO_PODIO_PROJETOR : padrao
}

/** Quantos representantes a festa de marca mostra. */
export function representantesNaFesta(projetor: boolean, padrao: number): number {
  return projetor ? REPRESENTANTES_NA_FESTA_PROJETOR : padrao
}
